import "./DailyWheelModal.scss";
import { useEffect, useRef, useState } from "react";
import { authStore, fortuneWheelStore } from "../../../../../platform/stores";
import { observer } from "mobx-react";
import { Wheel } from "../daily-wheel-button/wheel";
import { WheelPrize } from "../../../../../platform/interfaces/daily-wheel";
import { fortuneWheelService } from "../../../../../platform/services";
import { getCountDown } from "../../../../../platform/utils";

function useScaleToFit(ref: React.RefObject<HTMLElement>) {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!ref.current) return;

    const update = () => {
      if (!ref.current) return;
      const available = window.innerHeight * 0.9; // leave some vertical margin
      const naturalHeight = ref.current.scrollHeight; // transform doesn't affect scrollHeight
      const newScale = naturalHeight > available ? available / naturalHeight : 1;
      // avoid tiny jitter updates
      setScale((prev) => (Math.abs(prev - newScale) < 0.005 ? prev : newScale));
    };

    update();

    window.addEventListener("resize", update);
    const ro = new ResizeObserver(update);
    ro.observe(ref.current);

    return () => {
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, [ref]);

  return scale;
}

type SpinState = 'progress' | 'countdown' | 'ready' | 'loading';

export const DailyWheelModal = observer(() => {
  const [wonPrize, setWonPrize] = useState<WheelPrize | null>(null);
  const [countdown, setCountdown] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const scale = useScaleToFit(modalRef);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const wheelData = fortuneWheelStore.wheelData;
  const unusedSpins = fortuneWheelStore.unusedSpins;
  const lastSpinDate = fortuneWheelStore.lastSpinDate;
  const userProgress = fortuneWheelStore.userProgress;
  const loadingSpinState = fortuneWheelStore.loadingSpinState;

  // Determine current spin state
  const getSpinState = (): SpinState => {
    if (loadingSpinState || !wheelData) return 'loading';

    // State 3: User has unused spin and can spin
    if (unusedSpins.length > 0) {
      return 'ready';
    }

    // State 2: User needs to wait (countdown active)
    if (lastSpinDate) {
      const participationPeriod = wheelData.configuration?.participationPeriodSeconds || wheelData.participationPeriodSeconds || 86400;
      const countdownText = getCountDown(lastSpinDate, participationPeriod);
      if (countdownText) {
        return 'countdown';
      }
    }

    // State 1: User needs to make progress
    return 'progress';
  };

  const spinState = getSpinState();

  // Fetch spin state when modal opens
  useEffect(() => {
    if (fortuneWheelStore.dailyWheelModalIsOpen && wheelData) {
      fortuneWheelService.checkSpinState(wheelData.id);
    }
  }, [fortuneWheelStore.dailyWheelModalIsOpen, wheelData?.id]);

  // Update countdown timer
  useEffect(() => {
    if (spinState === 'countdown' && lastSpinDate && wheelData) {
      const participationPeriod = wheelData.configuration?.participationPeriodSeconds || wheelData.participationPeriodSeconds || 86400;

      const updateCountdown = () => {
        const countdownText = getCountDown(lastSpinDate, participationPeriod);
        setCountdown(countdownText);

        // If countdown is over, refresh spin state
        if (!countdownText) {
          if (wheelData) {
            fortuneWheelService.checkSpinState(wheelData.id);
          }
        }
      };

      updateCountdown();
      countdownIntervalRef.current = setInterval(updateCountdown, 1000);

      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      };
    } else {
      setCountdown(null);
    }
  }, [spinState, lastSpinDate, wheelData]);

  const handleSpinComplete = (prize: WheelPrize) => {
    setWonPrize(prize);
    console.log("Won prize:", prize);
    if (prize.type === 2 && prize.freeSpinQuantity > 0) {
      console.log(`Won ${prize.freeSpinQuantity} free spins!`);
    }
    // Keep prize result visible - don't auto-clear to show countdown
  };

  const handleCloseModal = () => {
    setIsClosing(true);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    // Delay actual closing to allow animation to complete
    setTimeout(() => {
      setIsClosing(false);
      setWonPrize(null);
      fortuneWheelStore.setDailyWheelModalIsOpen(false);
    }, 300);
  };

  const handleOpenInfoModal = () => {
    fortuneWheelStore.setForceShowNewFortuneWheelModal(true);
  };

  // close on escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseModal();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // prevent background scroll when modal is open
  useEffect(() => {
    if (fortuneWheelStore.dailyWheelModalIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [fortuneWheelStore.dailyWheelModalIsOpen]);

  if (!fortuneWheelStore.dailyWheelModalIsOpen || !wheelData) return null;

  const configuration = wheelData.configuration;
  const threshold = configuration?.progressCriteriaThreshold || 0;
  const progressCriteria = configuration?.progressCriteria || 1;
  const displayCurrency = configuration?.currency || wheelData.currency;
  const progressPercentage = threshold > 0 ? Math.min((userProgress / threshold) * 100, 100) : 0;
  const progressCriteriaName = progressCriteria === 1 ? 'Bet Total Amount' : 'Bet Count';
  const remaining = Math.max(threshold - userProgress, 0);
  const minBetAmount = configuration?.minBetAmount || 0;

  return (
    <div className={`daily-wheel-modal ${isClosing ? 'closing' : ''}`}>
      <div
        className="daily-wheel-modal-background"
        onClick={handleCloseModal}
      ></div>
      <div
        ref={modalRef}
        className={`daily-wheel-modal-content ${isClosing ? 'closing' : ''}`}
        style={{
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}
      >
        <div className="info-btn" onClick={handleOpenInfoModal} title="How it works">i</div>
        <div className="close-btn" onClick={handleCloseModal}>×</div>
        <h1>FORTUNE WHEEL</h1>

        {/* Wheel - Always shown */}
        {wheelData.prizes ? (
          <Wheel onSpinComplete={handleSpinComplete} onClose={handleCloseModal}/>
        ) : (
          <div className="loading-wheel">
            <p>Loading wheel...</p>
          </div>
        )}

        {wonPrize ? (
          <div className="prize-result">
            {(() => {
              // Check if user won something (not a default/empty prize)
              const hasFreeSpins = wonPrize.freeSpinQuantity > 0;
              const hasFreeBetAmount = wonPrize.freeSpinAmount > 0 && wonPrize.currency;
              const isWin = !wonPrize.isDefault && (hasFreeSpins || hasFreeBetAmount);
              
              if (isWin) {
                if (hasFreeSpins && hasFreeBetAmount) {
                  return (
                    <>
                      <h2>Congratulations!</h2>
                      <p>You won {wonPrize.freeSpinQuantity} free spins or {wonPrize.currency} {wonPrize.freeSpinAmount}!</p>
                    </>
                  );
                } else if (hasFreeSpins) {
                  return (
                    <>
                      <h2>Congratulations!</h2>
                      <p>You won {wonPrize.freeSpinQuantity} free spins!</p>
                    </>
                  );
                } else if (hasFreeBetAmount) {
                  return (
                    <>
                      <h2>Congratulations!</h2>
                      <p>You won {wonPrize.currency} {wonPrize.freeSpinAmount}!</p>
                    </>
                  );
                }
              }
              
              // Default: no win
              return (
                <>
                  <h2>Better Luck Next Time!</h2>
                  <p>You didn't win this time. Try again after the countdown ends!</p>
                </>
              );
            })()}
            <button onClick={handleCloseModal} className="close-button">
              CLOSE
            </button>
          </div>
        ) : loadingSpinState && unusedSpins.length === 0 ? (
          <div className="loading-wheel">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Progress Section - Shown only when not in countdown state and no unused spins */}
            {threshold > 0 && spinState !== 'countdown' && unusedSpins.length === 0 && (
              <div className="progress-state">
                <div className="progress-section">
                  <div className="progress-info">
                    <div className="progress-label">
                      <span>{progressCriteriaName}</span>
                      <span className="progress-value">
                        {progressCriteria === 1
                          ? `${displayCurrency} ${userProgress.toLocaleString()} / ${displayCurrency} ${threshold.toLocaleString()}`
                          : `${userProgress} / ${threshold}`
                        }
                      </span>
                    </div>
                  </div>

                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${Math.max(progressPercentage, 3)}%` }}
                    ></div>
                  </div>

                  <div className="progress-remaining">
                    {remaining > 0 ? (
                      <p>
                        {progressCriteria === 1
                          ? `You need ${displayCurrency} ${remaining.toLocaleString()} more${minBetAmount > 0 ? ` with min bet amount ${displayCurrency} ${minBetAmount.toLocaleString()}` : ''} to unlock your spin`
                          : `You need ${Math.max(threshold - userProgress, 0)} more bet${Math.max(threshold - userProgress, 0) !== 1 ? 's' : ''}${minBetAmount > 0 ? ` with min bet amount ${displayCurrency} ${minBetAmount.toLocaleString()}` : ''} to unlock your spin`
                        }
                      </p>
                    ) : (
                      <p>You've reached the threshold! Refresh to unlock your spin.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Countdown Section - Shown when countdown is active */}
            {spinState === 'countdown' && countdown && (
              <div className="countdown-state">
                <div className="countdown-header">
                  <h2>Wait for Next Spin</h2>
                  <p>You can spin again after the countdown ends</p>
                </div>

                <div className="countdown-display">
                  <div className="countdown-time">{countdown || '00:00:00'}</div>
                  <p className="countdown-label">Time remaining</p>
                </div>

                <div className="countdown-info">
                  <p>Come back when the countdown reaches zero to spin again!</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});
